<template>
  <div class="flex min-h-screen max-h-screen flex-col">
    <div v-if="!isLoading" class="flex-grow">
      <div class="flex mx-auto p-4 w-full justify-center" v-if="getBodyPrint">
        <div class="flex w-full">
          <div class="w-auto">
            <div class="border border-white">
              <crop-view
                v-if="current_step === 'crop'"
                :image-source="this.getImageBack"
                :projection="'back'"
                :is-active="current_projection === 'back'"
                :initial-crop="this.getGuide('back').crop"
                side="back"
                :flipped="this.back_image_flipped"
              />
              <bodyprint-view
                v-if="current_step === 'measure'"
                :image-source="this.getImageBack"
                :is-active="current_projection === 'back'"
                side="back"
                :flipped="this.back_image_flipped"
                :picture-height="getPictureHeight"
                :picture-width="getPictureWidth"
                :back-guide="getCropBack"
                :side-guide="getCropSide"
              />
            </div>
          </div>
          <div class="w-auto">
            <div class="border border-white">
              <crop-view
                v-if="current_step === 'crop'"
                :image-source="this.getImageSide"
                :projection="'left'"
                :is-active="current_projection === 'left'"
                :initial-crop="this.getGuide('left').crop"
                side="left"
                :flipped="side_image_flipped"
              />
              <bodyprint-view
                v-if="current_step === 'measure'"
                :image-source="this.getImageSide"
                :is-active="current_projection === 'left'"
                side="left"
                :flipped="side_image_flipped"
                :picture-height="getPictureHeight"
                :picture-width="getPictureWidth"
                :back-guide="getCropBack"
                :side-guide="getCropSide"
              />
            </div>
          </div>
          <div class="w-auto" v-if="current_step === 'measure'">
            <measure-set :image-flipped="imageFlippedSet()" />
          </div>
        </div>
        <div
          class="flex-grow flex flex-col justify-between bg-gray-300 -m-4 p-4"
        >
          <div class="flex-grow">
            <div class="text-xl font-semibold text-blue-800 mb-4">
              {{
                this.current_step === "crop"
                  ? $t("bodyprint_edit.crop_instructions")
                  : $t("bodyprint_edit.measure_instructions")
              }}
            </div>
            <div class="my-4">
              <crop-instructions
                v-if="current_step === 'crop'"
                :projection="current_projection"
              />
            </div>
            <div class="my-4">
              <measure-instructions v-if="current_step === 'measure'" />
            </div>
            <div class="my-4" v-if="getAuth.bodyprintList === true">
              <bodyprint-info />
            </div>
          </div>
          <!-- Controls and buttons -->
          <div class="flex flex-col">
            <controls />
            <div class="flex w-full bg-gray-200 mx-auto p-4">
              <div class="w-full grid grid-cols-3 gap-2">
                <button
                  class="col-span-1 button py-2 bg-gray-200 text-white"
                  :disabled="true"
                  v-if="
                    this.current_step === 'crop' &&
                      this.current_projection === 'back'
                  "
                ></button>
                <button
                  class="col-span-1 button py-2 bg-blue-800 text-white"
                  @click="goToPreviousStep"
                  v-else
                >
                  {{ getPreviousStepText() }}
                </button>
                <button
                  class="col-span-1 button py-2 bg-red-700 text-white"
                  @click="() => (showProblemModal = true)"
                >
                  {{ $t("bodyprint_edit.buttons.reject") }}
                </button>
                <button
                  :class="{
                    'col-span-1 button py-2 text-white': true,
                    'bg-blue-800': !isNextStepDisabled(),
                    'bg-gray-600': isNextStepDisabled()
                  }"
                  :disabled="isNextStepDisabled()"
                  @click="goToNextStep"
                >
                  {{ getNextStepText() }}
                </button>
                <div
                  v-if="
                    getAuth.blurEditSimple === true ||
                      getAuth.blurEditFull === true
                  "
                  class="col-span-3 grid grid-cols-3 gap-2"
                >
                  <button
                    class="col-span-3 button py-2 w-full bg-yellow-700 text-white"
                    @click="goToBlurEditor"
                  >
                    {{ $t("bodyprint_edit.blur_editor.edit_blur") }}
                  </button>
                </div>
                <div
                  class="col-span-3 grid grid-cols-3 gap-2"
                  v-show="this.current_step === 'crop'"
                >
                  <button
                    class="col-span-3 button py-2 w-full bg-blue-800 text-white"
                    @click="() => (side_image_flipped = !side_image_flipped)"
                  >
                    {{ $t("bodyprint_edit.buttons.flip_image") }}
                  </button>
                </div>
                <div
                  class="col-span-3 grid grid-cols-3 gap-2"
                  v-show="this.current_step === 'measure'"
                >
                  <button
                    class="col-span-3 button py-2 bg-blue-800 text-white"
                    @click="returnToCropping"
                  >
                    {{ $t("bodyprint_edit.buttons.back_to_crop") }}
                  </button>
                  <button
                    :class="{
                      'col-span-3 button py-2 text-white': true,
                      'bg-yellow-700 ': this.getBodyPrint.status === 'COMPLETE',
                      'bg-gray-600': this.getBodyPrint.status !== 'COMPLETE'
                    }"
                    :disabled="this.getBodyPrint.status !== 'COMPLETE'"
                    @click="() => prepareMeasurementsModal()"
                  >
                    {{ $t("bodyprint_edit.buttons.view_measurements") }}
                  </button>
                  <!--                <button-->
                  <!--                  class="button py-2 px-8 w-full bg-yellow-700 text-white mx-2"-->
                  <!--                  @click="showUpdateDialog = true"-->
                  <!--                >-->
                  <!--                  Update bodyprint-->
                  <!--                </button>-->
                  <button
                    :class="{
                      'col-span-3 button py-2 text-white': true,
                      'bg-blue-800': !isFinalizeDisabled(),
                      'bg-gray-600': isFinalizeDisabled()
                    }"
                    :disabled="isFinalizeDisabled()"
                    @click="showSaveDialog = true"
                    v-show="this.isQueueItem"
                  >
                    {{ $t("bodyprint_edit.buttons.finalize") }}
                  </button>
                </div>
                <button
                  class="col-span-3 button py-2 bg-red-700 text-white"
                  @click="showCancelDialog = true"
                  v-show="this.isQueueItem"
                >
                  {{ $t("bodyprint_edit.buttons.return") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col mx-auto p-4 w-full h-full items-center text-blue-800 text-2xl my-12"
      style="text-align: center"
    >
      {{ $t("bodyprint_edit.loading") }}
    </div>
    <div>
      <problem-modal
        :showModal="showProblemModal"
        :onClose="() => (showProblemModal = false)"
        :onSubmit="reason => rejectBodyPrint(reason)"
      />
    </div>
    <div>
      <JSON-modal
        :beautifiedJSON="beautifiedJSON"
        :showModal="showJSONModal"
        :onClose="() => (showJSONModal = false)"
      />
    </div>
    <div>
      <measurements-modal
        :loading="measurementsLoading"
        :showModal="showMeasurementsModal"
        :data="measurementsBodyprint"
        :onClose="
          () => {
            showMeasurementsModal = false;
            measurementsBodyprint = null;
          }
        "
      />
    </div>
    <return-bodyprint-modal
      :show-modal="showCancelDialog"
      :title="$t('bodyprint_edit.dialogs.return.title').toString()"
      :message="$t('bodyprint_edit.dialogs.return.message').toString()"
      :on-continue="() => (showCancelDialog = false)"
      :on-save="
        () => {
          showCancelDialog = false;
          this.saveChangesInBodyprint();
        }
      "
      :on-cancel="
        () => {
          showCancelDialog = false;
          showCancelConfirmationDialog = true;
        }
      "
    />
    <Dialog
      v-if="showCancelConfirmationDialog"
      @confirmed="
        () => {
          showCancelConfirmationDialog = false;
          this.stopEditing();
        }
      "
      @close="
        () => {
          showCancelConfirmationDialog = false;
          showCancelDialog = true;
        }
      "
      :title="$t('bodyprint_edit.dialogs.cancel_confirmation.title').toString()"
      :message="
        $t('bodyprint_edit.dialogs.cancel_confirmation.message').toString()
      "
      color="red"
    />
    <Dialog
      v-if="showSaveDialog"
      @confirmed="completeBodyPrint"
      @close="showSaveDialog = false"
      :title="$t('bodyprint_edit.dialogs.save.title').toString()"
      :message="$t('bodyprint_edit.dialogs.save.message').toString()"
    />
    <session-timeout-modal
      :show-modal="showSessionTimeoutModal"
      :title="$t('bodyprint_edit.dialogs.session_timeout.title').toString()"
      :message="$t('bodyprint_edit.dialogs.session_timeout.message').toString()"
      :close-text="
        $t('bodyprint_edit.dialogs.session_timeout.close').toString()
      "
      :on-close="closeSessionTimeoutModal"
    />
  </div>
</template>

<script>
import CropView from "../../components/crop/crop-view.vue";
import Dialog from "../../components/dialog.vue";
import MeasureSet from "../../components/measure/measure-set.vue";
import Controls from "../../components/controls.vue";
import { mapGetters, mapMutations, mapActions } from "vuex";
import beautify from "json-beautify";
import CropInstructions from "../../components/crop/crop-instructions.vue";
import JSONModal from "../../components/modals/json-modal.vue";
import ProblemModal from "../../components/modals/problem-modal.vue";
import MeasurementsModal from "../../components/modals/measurements-modal.vue";
import ReturnBodyprintModal from "../../components/modals/return-bodyprint-modal.vue";
import SessionTimeoutModal from "../../components/modals/session-timeout-modal.vue";
import MeasureInstructions from "../../components/measure/measure-instructions.vue";
import { ROUTE_BODYPRINT_BLUR_EDIT, ROUTE_BODYPRINT_QUEUE } from "@/router";
import BodyprintView from "@/components/bodyprint/bodyprint-view.vue";
import BodyprintInfo from "@/components/bodyprint/bodyprint-info.vue";
import { BodyprintQueueStatus } from "@/enums/modules/BodyprintQueueStatus";
import { BlurQueueStatus } from "@/enums/modules/BlurQueueStatus";

const SESSION_TIMEOUT_MS = 60 * 15 * 1000;

export default {
  name: "BodyprintEditView",
  components: {
    BodyprintInfo,
    BodyprintView,
    CropView,
    Dialog,
    MeasureSet,
    Controls,
    JSONModal,
    ProblemModal,
    MeasurementsModal,
    MeasureInstructions,
    CropInstructions,
    ReturnBodyprintModal,
    SessionTimeoutModal
  },
  created() {
    this.startSessionTimer();

    const { step, projection, measure_step } = this.$route.query;
    const returningFromBlurEditor = !!(
      step ||
      projection ||
      measure_step !== undefined
    );
    if (step) this.current_step = step;
    if (projection) this.current_projection = projection;
    if (measure_step !== undefined) this.measure_step = Number(measure_step);
    if (returningFromBlurEditor) {
      this.$router.replace({ ...this.$route, query: {} });
    }

    if (!this.getBodyPrint || returningFromBlurEditor) {
      this.getViewData();
      return;
    } else {
      this.finishGetViewData();
    }

    this.isLoading = false;

    this.setImageWindowHeight();
    window.addEventListener("resize", this.setImageWindowHeight);
    window.addEventListener("keydown", this.handleKeydown, null);
  },
  beforeDestroy() {
    window.removeEventListener("resize", () => {});
    window.removeEventListener("keydown", this.handleKeydown);
    clearTimeout(this.sessionTimer);
  },
  computed: {
    ...mapGetters({
      getAuth: "auth/getAuth",
      getArea: "configuration/getArea",
      getAreas: "configuration/getAreas",
      getPointsByArea: "configuration/getPointsByArea",
      getMeasureStep: "bodyprint/getMeasureStep",
      getGuide: "bodyprint/getGuide",
      getBodyPrint: "bodyprint/getBodyPrint",
      getQueueItem: "bodyprint/getQueueItem",
      isQueueItem: "bodyprint/isQueueItem",
      getImageSide: "bodyprint/getImageSide",
      getImageBack: "bodyprint/getImageBack",
      getPictureHeight: "bodyprint/getPictureHeight",
      getPictureWidth: "bodyprint/getPictureWidth",
      getCropBack: "bodyprint/getCropBack",
      getCropSide: "bodyprint/getCropSide"
    }),
    showCrop() {
      return this.current_step === "start" || this.current_step === "crop";
    },
    checkCrop() {
      let crop = this.getGuide(this.current_projection).crop;
      let bodyprint = this.getBodyPrint;
      return !!(bodyprint.points.length > 0 || crop.x2);
    },
    currentStep() {
      if (this.current_step === "start") {
        return this.$t("bodyprint_edit.current_step.start");
      }
      if (this.current_step === "review") {
        return this.$t("bodyprint_edit.current_step.review");
      }
      if (this.current_step === "crop") {
        return this.$t("bodyprint_edit.current_step.crop");
      }
      if (this.current_step === "measure") {
        return this.$t("bodyprint_edit.current_step.measure");
      }
      return "";
    },
    // currentImage() {
    //     return this.getGuide(this.current_projection).image_source
    // },
    beautifiedJSON() {
      return beautify(this.$store.getters["bodyPrint"], null, 2, 80);
    }
  },
  data() {
    return {
      isLoading: true,
      measurementsLoading: false,
      showSaveDialog: false,
      showCancelDialog: false,
      showCancelConfirmationDialog: false,
      rejectionReason: "",
      token: "",
      showProblemModal: false,
      showJSONModal: false,
      showMeasurementsModal: false,
      measurementsBodyprint: null,
      bodyprint: null,
      current_step: "crop",
      current_projection: "back",
      measure_step: 0,
      image_source_back: "lady_orange_back.png",
      image_source_side: "lady_orange_side.png",
      back_crop: {
        x: 0.27,
        y: 0.18,
        width: 0.47,
        height: 0.66,
        projection: "back"
      },
      side_crop: {
        x: 0.31,
        y: 0.2,
        width: 0.44,
        height: 0.6,
        projection: "left"
      },
      back_image_flipped: false,
      side_image_flipped: false,
      isPostingBodyprint: false,
      showSessionTimeoutModal: false,
      sessionTimer: null
    };
  },
  watch: {
    currentStep() {
      this.setCurrentStep(this.current_step);
    }
  },
  methods: {
    ...mapMutations({
      setActiveCoordinates: "bodyprint/setActiveCoordinates",
      setWindowHeight: "bodyprint/setWindowHeight",
      setCurrentStep: "bodyprint/setCurrentStep",
      setMeasureStep: "bodyprint/setMeasureStep",
      updateConstraints: "bodyprint/updateConstraints"
    }),
    ...mapActions(["auth/authLogout", "layout/toggleSidebar"]),
    ...mapActions({
      postBodyprint: "bodyprint/postBodyprint",
      setQueueItemStatus: "bodyprint/setQueueItemStatus",
      cancelQueueItem: "bodyprint/cancelQueueItem",
      getBodyprintSizes: "bodyprint/getBodyprintSizes",
      recalculatePoints: "bodyprint/recalculatePoints",
      getBodyprintMeasurements: "bodyprint/getBodyprintMeasurements"
    }),
    getViewData() {
      const queueId = this.$route.params.queue_id;
      this.$store
        .dispatch("bodyprint/requestBodyprint", {
          id: queueId
        })
        .then(() => {
          var bp = this.$store.getters["bodyprint/getBodyPrint"];
          if (bp.user) {
            this.$store.dispatch("bodyprint/getBodyprintSizes", {
              user: bp.user,
              bodyprint: bp._id
            });
            this.$store
              .dispatch("bodyprint/getBlurredImages", bp.user)
              .then(() => {
                this.setImageWindowHeight();
                this.$store.dispatch("bodyprint/editBodyprint").then(() => {
                  if (this.getAuth.bodyprintList) {
                    this.$store
                      .dispatch("bodyprint/requestBodyprintInfo", {
                        id: queueId
                      })
                      .then(() => {
                        this.finishGetViewData();
                        if (this.current_step === "measure") {
                          this.recalculatePoints();
                        }
                      });
                  } else {
                    this.finishGetViewData();
                    if (this.current_step === "measure") {
                      this.recalculatePoints();
                    }
                  }
                });
              })
              .catch(() => {
                this.toEndScreen();
              });
          }
        })
        .catch(() => {
          this.isLoading = false;
          this.toEndScreen();
        });
    },
    finishGetViewData() {
      this.isLoading = false;
      if (this.current_step === "measure") {
        this.setStepMeasure();
      }
      const bp = this.getBodyPrint;
      if (bp && bp.points) {
        bp.points.forEach(point => {
          this.$store.dispatch("bodyprint/setModifiedPoint", point.name);
        });
      }
    },
    setImageWindowHeight() {
      let payload = {
        height: window.innerHeight,
        width: window.innerWidth
      };
      this.setWindowHeight(payload);
    },
    getPreviousStepText() {
      if (this.current_step === "crop") {
        if (this.current_projection === "back") {
          return "";
        } else if (this.current_projection === "left") {
          return this.$t("bodyprint_edit.buttons.back_crop");
        }
      } else if (this.current_step === "measure") {
        return this.$t("bodyprint_edit.buttons.previous");
      }
    },
    getNextStepText() {
      if (this.current_step === "crop") {
        if (this.current_projection === "back") {
          return this.$t("bodyprint_edit.buttons.side_crop");
        } else if (this.current_projection === "left") {
          return this.$t("bodyprint_edit.buttons.measure");
        }
      } else if (this.current_step === "measure") {
        return this.$t("bodyprint_edit.buttons.next");
      }
    },
    returnToCropping() {
      this.current_projection = "left";
      this.current_step = "crop";
    },
    goToPreviousStep() {
      this.setActiveCoordinates({}); //reset activeCoordinates
      if (this.current_step === "crop") {
        if (this.current_projection === "left") {
          this.current_projection = "back";
        }
      } else if (this.current_step === "measure") {
        this.previousMeasureStep();
        this.updateBodyprint();
      }
    },
    goToNextStep() {
      this.setActiveCoordinates({}); //reset activeCoordinates
      if (this.current_step === "crop") {
        if (this.current_projection === "back") {
          this.current_projection = "left";
        } else if (this.current_projection === "left") {
          this.current_projection = "back";
          this.current_step = "measure";
          this.measure_step = 0;
          this.setStepMeasure();
          this.recalculatePoints();
        }
        this.updateBodyprint();
      } else if (this.current_step === "measure") {
        this.getPointsByArea.forEach(point => {
          this.$store.dispatch(
            "bodyprint/setModifiedPoint",
            point.puppetPointName
          );
        });
        this.nextMeasureStep();
        this.updateBodyprint();
      }
    },
    nextMeasureStep() {
      this.measure_step = this.getMeasureStep + 1;
      this.setStepMeasure();
    },
    previousMeasureStep() {
      this.measure_step = this.getMeasureStep - 1;
      this.setStepMeasure();
    },
    setStepMeasure() {
      const payload = {
        areas: this.getAreas,
        step: this.measure_step,
        projection: this.current_projection
      };
      this.updateConstraints(payload);
      this.setMeasureStep(payload);
    },
    rejectBodyPrint(reason) {
      this.showProblemModal = false;
      var payload = {
        status: BodyprintQueueStatus.REJECTED,
        rejection_reason: reason
      };
      this.setQueueItemStatus(payload).then(() => this.toEndScreen());
    },
    stopEditing() {
      this.cancelQueueItem().then(() => this.toEndScreen());
    },
    saveChangesInBodyprint() {
      if (this.isPostingBodyprint) return;

      this.isPostingBodyprint = true;
      this.postBodyprint({ onlyModified: true }).then(() => {
        const bodyprint = this.$store.getters["bodyprint/getBodyPrint"];
        const queueItem = this.$store.getters["bodyprint/getQueueItem"];

        const completedStatus =
          queueItem.status === BodyprintQueueStatus.COMPLETED ||
          queueItem.status === BodyprintQueueStatus.COMPLETED_IN_REVIEW ||
          queueItem.status === BodyprintQueueStatus.COMPLETED_IN_PROGRESS;

        const status =
          bodyprint && bodyprint.blur_status === BlurQueueStatus.REJECTED
            ? BodyprintQueueStatus.REJECTED
            : completedStatus
            ? BodyprintQueueStatus.COMPLETED_IN_PROGRESS
            : BodyprintQueueStatus.IN_PROGRESS;

        this.setQueueItemStatus({ status }).then(() => {
          this.isPostingBodyprint = false;
          this.toEndScreen();
        });
      });
    },
    completeBodyPrint() {
      if (this.isPostingBodyprint) return;

      this.isPostingBodyprint = true;
      this.postBodyprint().then(() => {
        var payload = { status: BodyprintQueueStatus.COMPLETED };
        this.setQueueItemStatus(payload).then(() => {
          this.isPostingBodyprint = false;
          this.toEndScreen();
        });
      });
    },
    startSessionTimer() {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = setTimeout(() => {
        this.showSessionTimeoutModal = true;
      }, SESSION_TIMEOUT_MS);
    },
    closeSessionTimeoutModal() {
      this.showSessionTimeoutModal = false;
      clearTimeout(this.sessionTimer);
      this.toEndScreen();
    },
    updateBodyprint() {
      if (this.isPostingBodyprint) return;

      this.startSessionTimer();
      this.isPostingBodyprint = true;
      this.postBodyprint({ onlyModified: true }).then(() => {
        this.isPostingBodyprint = false;
        this.measurementsLoading = true;
        const params = {
          user: this.getBodyPrint.user,
          bodyprint: this.getBodyPrint._id
        };
        setTimeout(() => {
          this.getBodyprintSizes(params).then(() => {
            this.measurementsLoading = false;
            this.measurementsBodyprint = this.$store.getters[
              "bodyprint/getBodyprintSizes"
            ];
          });
        }, 1000);
      });
    },
    toEndScreen() {
      this.$router.replace({
        name: ROUTE_BODYPRINT_QUEUE
      });
      this.$store.dispatch("layout/toggleSidebar");
    },
    handleKeydown(e) {
      switch (e.code) {
        case "Enter":
          e.preventDefault();
          this.goToNextStep();
          break;
        case "Backspace":
          e.preventDefault();
          this.goToPreviousStep();
          break;
      }
    },
    prepareMeasurementsModal() {
      this.showMeasurementsModal = true;
      this.measurementsLoading = true;

      this.getBodyprintMeasurements().then(() => {
        setTimeout(() => {
          this.measurementsLoading = false;
          this.measurementsBodyprint = this.$store.getters[
            "bodyprint/getBodyprintSizes"
          ];
        }, 1000);
      });
    },
    imageFlippedSet() {
      if (this.getArea.projection === "left") {
        return this.side_image_flipped;
      } else if (this.getArea.projection === "back") {
        return this.back_image_flipped;
      }
      return true;
    },
    isNextStepDisabled() {
      const bp = this.$store.getters["bodyprint/getBodyPrint"];
      if (this.current_step === "crop") {
        if (this.current_projection === "left") {
          return !bp || bp.blur_status === BlurQueueStatus.REJECTED;
        }
      }
      return false;
    },
    isFinalizeDisabled() {
      const bp = this.$store.getters["bodyprint/getBodyPrint"];
      return (
        !bp ||
        bp.blur_status !== BlurQueueStatus.BLURRED ||
        bp.status !== "COMPLETE"
      );
    },
    goToBlurEditor() {
      this.$router.push({
        name: ROUTE_BODYPRINT_BLUR_EDIT,
        params: { queue_id: this.$route.params.queue_id },
        query: {
          step: this.current_step,
          projection: this.current_projection,
          measure_step: this.measure_step
        }
      });
    }
  }
};
</script>

<style></style>
